<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { api } from '@/utils/api';

  interface ProfileData {
    id: string;
    username: string | null;
    name: string;
    bio: string | null;
    social_links: Record<string, string>;
    public_url: string | null;
    username_changed_at: string | null;
    page_theme: ThemeData;
  }

  interface ThemeData {
    template: string;
    bg_color: string | null;
    text_color: string | null;
    accent_color: string | null;
    bg_image_url: string | null;
    custom_css: Record<string, string>;
  }

  interface LinkData {
    id: string;
    title: string;
    url: string;
    description: string | null;
    emoji: string | null;
    link_type: string;
    position: number;
    is_active: boolean;
    clicks_count: number;
  }

  interface ShortLinkData {
    id: string;
    slug: string;
    original_url: string;
    short_url: string;
    title: string | null;
    is_active: boolean;
    clicks_count: number;
    created_at: string;
  }

  let {
    profile,
    links = [],
    short_links: initialShortLinks = [],
  }: {
    profile: ProfileData;
    links: LinkData[];
    short_links: ShortLinkData[];
  } = $props();

  const TEMPLATES = [
    { key: 'clean', name: '클린 화이트', category: '기본' },
    { key: 'dark', name: '다크 모드', category: '기본' },
    { key: 'glass', name: '글래스모피즘', category: '크리에이티브' },
    { key: 'aurora', name: '오로라', category: '크리에이티브' },
    { key: 'corporate', name: '비즈니스 프로', category: '비즈니스' },
    { key: 'bento', name: '벤토 그리드', category: '크리에이티브' },
    { key: 'minimal', name: '미니멀리스트', category: '기본' },
    { key: 'ocean', name: '오션 블루', category: '크리에이티브' },
    { key: 'neubrutalism', name: '뉴브루탈리즘', category: '크리에이티브' },
    { key: 'nature', name: '내추럴', category: '비즈니스' },
  ] as const;

  const SOCIAL_PLATFORMS = [
    { key: 'email', label: '이메일', placeholder: 'email@example.com' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/...' },
    { key: 'github', label: 'GitHub', placeholder: 'https://github.com/...' },
    { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/...' },
    { key: 'website', label: '웹사이트', placeholder: 'https://...' },
  ] as const;

  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let username = $state(profile.username || '');
  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let bio = $state(profile.bio || '');
  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let selectedTemplate = $state(profile.page_theme.template || 'clean');
  // svelte-ignore state_referenced_locally
  const initialProfile = profile;
  // svelte-ignore state_referenced_locally
  const initialLinks = links;
  // svelte-ignore state_referenced_locally
  const initialShortLinkItems = initialShortLinks;
  let socialLinks = $state<Record<string, string>>({ ...initialProfile.social_links });
  let profileLinks = $state<LinkData[]>([...initialLinks]);
  let shortLinks = $state<ShortLinkData[]>([...initialShortLinkItems]);
  let activeTab = $state<'theme' | 'links' | 'shorts'>('theme');
  let isSaving = $state(false);
  let isCreatingLink = $state(false);
  let isCreatingShortLink = $state(false);

  let newLinkTitle = $state('');
  let newLinkUrl = $state('');
  let newLinkDescription = $state('');
  let newLinkEmoji = $state('');
  let newLinkType = $state<'external' | 'post' | 'file'>('external');

  let newShortUrl = $state('');
  let newShortTitle = $state('');

  let showSaveSuccess = $state(false);

  function canChangeUsername(): boolean {
    if (!profile.username_changed_at) return true;
    return new Date(profile.username_changed_at) <= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  }

  function usernameChangeCooldown(): string | null {
    if (!profile.username_changed_at) return null;
    const next = new Date(profile.username_changed_at);
    next.setDate(next.getDate() + 30);
    if (next <= new Date()) return null;
    const diff = next.getTime() - Date.now();
    const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
    return `${days}일 후 변경 가능`;
  }

  async function saveProfile() {
    isSaving = true;
    try {
      const response = await api.patch('/api/v1/my_profile', {
        profile: {
          username: username || null,
          bio: bio || null,
          social_links: socialLinks,
          page_theme: {
            template: selectedTemplate,
          },
        },
      });

      if (response.success) {
        showSaveSuccess = true;
        setTimeout(() => (showSaveSuccess = false), 3000);
        router.reload();
      }
    } finally {
      isSaving = false;
    }
  }

  async function addLink() {
    if (!newLinkTitle.trim() || !newLinkUrl.trim()) return;

    isCreatingLink = true;
    try {
      const response = await api.post<{ link: LinkData }>('/api/v1/profile_links', {
        profile_link: {
          title: newLinkTitle.trim(),
          url: newLinkUrl.trim(),
          description: newLinkDescription.trim() || null,
          emoji: newLinkEmoji.trim() || null,
          link_type: newLinkType,
        },
      });

      if (response.success && response.data) {
        profileLinks = [...profileLinks, response.data.link];
        newLinkTitle = '';
        newLinkUrl = '';
        newLinkDescription = '';
        newLinkEmoji = '';
        newLinkType = 'external';
      }
    } finally {
      isCreatingLink = false;
    }
  }

  async function deleteLink(id: string) {
    const response = await api.delete(`/api/v1/profile_links/${id}`);
    if (response.success) {
      profileLinks = profileLinks.filter((l) => l.id !== id);
    }
  }

  async function toggleLinkActive(link: LinkData) {
    const response = await api.patch(`/api/v1/profile_links/${link.id}`, {
      profile_link: { is_active: !link.is_active },
    });
    if (response.success) {
      profileLinks = profileLinks.map((l) =>
        l.id === link.id ? { ...l, is_active: !l.is_active } : l
      );
    }
  }

  async function addShortLink() {
    if (!newShortUrl.trim()) return;

    isCreatingShortLink = true;
    try {
      const response = await api.post<{ shortLink: ShortLinkData }>('/api/v1/short_links', {
        short_link: {
          original_url: newShortUrl.trim(),
          title: newShortTitle.trim() || null,
        },
      });

      if (response.success && response.data) {
        shortLinks = [response.data.shortLink, ...shortLinks];
        newShortUrl = '';
        newShortTitle = '';
      }
    } finally {
      isCreatingShortLink = false;
    }
  }

  async function deleteShortLink(id: string) {
    const response = await api.delete(`/api/v1/short_links/${id}`);
    if (response.success) {
      shortLinks = shortLinks.filter((s) => s.id !== id);
    }
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback
    }
  }
</script>

<div class="space-y-6">
    <div class="mb-6">
      <h1 class="text-h1 text-content">프로필 설정</h1>
      <p class="text-body2 text-content-secondary mt-1">
        공개 프로필, 테마, 링크를 관리하세요
      </p>
    </div>

    {#if showSaveSuccess}
      <div
        class="mb-4 p-3 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2"
      >
        프로필이 저장되었습니다.
      </div>
    {/if}

    <!-- Public URL -->
    {#if profile.public_url}
      <div class="card p-4">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-caption text-content-tertiary">공개 프로필 URL</p>
            <a
              href={profile.public_url}
              target="_blank"
              rel="noopener noreferrer"
              class="text-body2 text-primary hover:underline font-mono"
            >
              {profile.public_url}
            </a>
          </div>
          <button
            class="btn btn-secondary btn-sm"
            onclick={() => copyToClipboard(window.location.origin + profile.public_url)}
          >
            복사
          </button>
        </div>
      </div>
    {/if}

    <!-- Tabs -->
    <div class="flex gap-1 border-b border-default">
      <button
        class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
        class:border-primary={activeTab === 'theme'}
        class:border-transparent={activeTab !== 'theme'}
        class:text-primary={activeTab === 'theme'}
        class:text-gray-500={activeTab !== 'theme'}
        onclick={() => (activeTab = 'theme')}
      >
        기본 정보
      </button>
      <button
        class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
        class:border-primary={activeTab === 'links'}
        class:border-transparent={activeTab !== 'links'}
        class:text-primary={activeTab !== 'links'}
        class:text-gray-500={activeTab !== 'links'}
        onclick={() => (activeTab = 'links')}
      >
        프로필 링크
      </button>
      <button
        class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
        class:border-primary={activeTab === 'shorts'}
        class:border-transparent={activeTab !== 'shorts'}
        class:text-primary={activeTab !== 'shorts'}
        class:text-gray-500={activeTab !== 'shorts'}
        onclick={() => (activeTab = 'shorts')}
      >
        Short URL
      </button>
    </div>

    <!-- Theme Tab -->
    {#if activeTab === 'theme'}
      <div class="space-y-6 max-w-2xl">
        <!-- Username -->
        <div class="card p-6">
          <h2 class="text-label text-content mb-4">Username</h2>
          <div class="mb-3">
            <label
              for="username"
              class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1"
            >
              Username
            </label>
            <div class="flex items-center gap-2">
              <span class="text-gray-400">@</span>
              <input
                type="text"
                id="username"
                bind:value={username}
                maxlength="30"
                pattern="[a-z0-9_]*"
                class="input"
                placeholder="username"
                disabled={!canChangeUsername()}
              />
            </div>
            <p class="mt-1 text-caption text-gray-400">영문 소문자, 숫자, 언더스코어 (3~30자)</p>
            {#if !canChangeUsername()}
              <p class="mt-1 text-caption text-warning">
                {usernameChangeCooldown()}
              </p>
            {/if}
          </div>

          <!-- Bio -->
          <div>
            <label for="bio" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1"
              >소개</label
            >
            <textarea
              id="bio"
              bind:value={bio}
              maxlength="200"
              rows="3"
              class="input"
              placeholder="간단한 소개를 입력하세요"
            ></textarea>
            <p class="mt-1 text-caption text-gray-400 text-right">{bio.length}/200</p>
          </div>
        </div>

        <!-- Social Links -->
        <div class="card p-6">
          <h2 class="text-label text-content mb-4">소셜 링크</h2>
          <div class="space-y-3">
            {#each SOCIAL_PLATFORMS as platform}
              <div>
                <label
                  for="social-{platform.key}"
                  class="block text-sm text-content-secondary mb-1"
                >
                  {platform.label}
                </label>
                <input
                  type="text"
                  id="social-{platform.key}"
                  bind:value={socialLinks[platform.key]}
                  placeholder={platform.placeholder}
                  class="input"
                  oninput={(e) =>
                    (socialLinks[platform.key] = (e.target as HTMLInputElement).value)}
                />
              </div>
            {/each}
          </div>
        </div>

        <!-- Theme Selection -->
        <div class="card p-6">
          <h2 class="text-label text-content mb-4">테마</h2>
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {#each TEMPLATES as tmpl}
              <button
                type="button"
                class="relative p-3 border-2 rounded-xl text-center transition-all hover:shadow-md"
                class:border-primary={selectedTemplate === tmpl.key}
                class:border-gray-200={selectedTemplate !== tmpl.key}
                class:dark:border-dark-border={selectedTemplate !== tmpl.key}
                class:bg-primary-50={selectedTemplate === tmpl.key}
                class:dark:bg-primary-10={selectedTemplate === tmpl.key}
                onclick={() => (selectedTemplate = tmpl.key)}
              >
                <span class="text-sm font-medium text-content"
                  >{tmpl.name}</span
                >
                <span class="block text-xs text-gray-400 mt-0.5">{tmpl.category}</span>
              </button>
            {/each}
          </div>
        </div>

        <!-- Button Style moved to Profile Editor Design tab (Buttons section: variant × roundness × shadow). -->

        <!-- Save -->
        <div class="flex justify-end">
          <button class="btn btn-primary btn-md" disabled={isSaving} onclick={saveProfile}>
            {isSaving ? '저장 중...' : '프로필 저장'}
          </button>
        </div>
      </div>
    {/if}

    <!-- Links Tab -->
    {#if activeTab === 'links'}
      <div class="space-y-4 max-w-2xl">
        <!-- Add Link Form -->
        <div class="card p-6">
          <h2 class="text-label text-content mb-4">링크 추가</h2>
          <div class="space-y-3">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label for="new-link-title" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">제목 *</label>
                <input
                  id="new-link-title"
                  type="text"
                  bind:value={newLinkTitle}
                  maxlength="100"
                  class="input"
                  placeholder="링크 제목"
                />
              </div>
              <div>
                <label for="new-link-url" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">URL *</label>
                <input
                  id="new-link-url"
                  type="text"
                  bind:value={newLinkUrl}
                  maxlength="2048"
                  class="input"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label for="new-link-emoji" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">이모지</label>
                <input
                  id="new-link-emoji"
                  type="text"
                  bind:value={newLinkEmoji}
                  maxlength="10"
                  class="input"
                  placeholder="📄"
                />
              </div>
              <div>
                <label for="new-link-type" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">타입</label>
                <select id="new-link-type" bind:value={newLinkType} class="input">
                  <option value="external">외부 URL</option>
                  <option value="post">게시글</option>
                  <option value="file">파일</option>
                </select>
              </div>
              <div class="flex items-end">
                <button
                  class="btn btn-primary btn-md w-full"
                  disabled={isCreatingLink || !newLinkTitle.trim() || !newLinkUrl.trim()}
                  onclick={addLink}
                >
                  {isCreatingLink ? '추가 중...' : '추가'}
                </button>
              </div>
            </div>
            <div>
              <label for="new-link-description" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">설명</label>
              <input
                id="new-link-description"
                type="text"
                bind:value={newLinkDescription}
                maxlength="200"
                class="input"
                placeholder="간단한 설명 (선택)"
              />
            </div>
          </div>
        </div>

        <!-- Links List -->
        <div class="card">
          <div class="p-4 border-b border-default">
            <h2 class="text-label text-content">
              링크 목록 ({profileLinks.length}/50)
            </h2>
          </div>
          {#if profileLinks.length === 0}
            <div class="p-8 text-center text-gray-400 dark:text-dark-text-disabled">
              아직 링크가 없습니다. 링크를 추가해보세요.
            </div>
          {:else}
            <div class="divide-y divide-gray-100 dark:divide-dark-border">
              {#each profileLinks as link, i}
                <div
                  class="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-dark-tertiary/30 transition-colors"
                >
                  <span class="text-lg w-8 text-center flex-shrink-0">
                    {link.emoji || i + 1}
                  </span>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-content truncate">
                      {link.title}
                    </p>
                    <p class="text-xs text-gray-400 truncate">
                      {link.url}
                    </p>
                  </div>
                  <span class="text-xs text-gray-400 flex-shrink-0">
                    {link.clicks_count}회 클릭
                  </span>
                  <button
                    class="text-xs px-2 py-1 rounded-full flex-shrink-0"
                    class:bg-success-light={link.is_active}
                    class:dark:bg-success-15={link.is_active}
                    class:bg-gray-100={!link.is_active}
                    class:dark:bg-dark-tertiary={!link.is_active}
                    class:text-success-dark={link.is_active}
                    class:text-gray-500={!link.is_active}
                    onclick={() => toggleLinkActive(link)}
                  >
                    {link.is_active ? '활성' : '비활성'}
                  </button>
                  <button
                    class="text-gray-400 hover:text-error flex-shrink-0"
                    onclick={() => deleteLink(link.id)}
                    aria-label={`${link.title} 삭제`}
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Short URLs Tab -->
    {#if activeTab === 'shorts'}
      <div class="space-y-4 max-w-2xl">
        <!-- Create Short Link -->
        <div class="card p-6">
          <h2 class="text-label text-content mb-4">Short URL 생성</h2>
          <div class="space-y-3">
            <div>
              <label for="new-short-url" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">원본 URL *</label>
              <input
                id="new-short-url"
                type="text"
                bind:value={newShortUrl}
                class="input"
                placeholder="https://... 또는 /@username"
              />
            </div>
            <div>
              <label for="new-short-title" class="block text-sm text-gray-700 dark:text-dark-text-medium mb-1">이름 (선택)</label>
              <input
                id="new-short-title"
                type="text"
                bind:value={newShortTitle}
                maxlength="100"
                class="input"
                placeholder="관리용 이름"
              />
            </div>
            <button
              class="btn btn-primary btn-md"
              disabled={isCreatingShortLink || !newShortUrl.trim()}
              onclick={addShortLink}
            >
              {isCreatingShortLink ? '생성 중...' : '생성'}
            </button>
          </div>
        </div>

        <!-- Short Links List -->
        <div class="card">
          <div class="p-4 border-b border-default">
            <h2 class="text-label text-content">
              Short URL 목록 ({shortLinks.length}/100)
            </h2>
          </div>
          {#if shortLinks.length === 0}
            <div class="p-8 text-center text-gray-400 dark:text-dark-text-disabled">
              아직 Short URL이 없습니다.
            </div>
          {:else}
            <div class="divide-y divide-gray-100 dark:divide-dark-border">
              {#each shortLinks as sLink}
                <div
                  class="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-dark-tertiary/30 transition-colors"
                >
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-content truncate">
                      {sLink.title || sLink.original_url}
                    </p>
                    <p class="text-xs text-gray-400">
                      <a
                        href={sLink.short_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary hover:underline"
                      >
                        {window.location.origin}{sLink.short_url}
                      </a>
                      {' → '}
                      <span class="truncate">{sLink.original_url}</span>
                    </p>
                    <p class="text-xs text-gray-400">
                      {sLink.clicks_count}회 클릭
                    </p>
                  </div>
                  <button
                    class="text-gray-400 hover:text-error flex-shrink-0"
                    onclick={() => deleteShortLink(sLink.id)}
                    aria-label={`${sLink.title || sLink.original_url} 삭제`}
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Back -->
    <div class="flex justify-center mt-8">
      <a href="/settings" class="btn btn-secondary btn-md">
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        설정으로 돌아가기
      </a>
    </div>
  </div>
